// Rotate either side of a centerline joint, preserving the pivot and every segment's length.
export function rotateFreeFormContinuation(points, jointIndex, radians, side = 'after') {
  const pivot = points[jointIndex];
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  return points.map((point, index) => {
    const shouldRotate = side === 'before' ? index < jointIndex : index > jointIndex;
    if (!shouldRotate) {
      return {x: point.x, y: point.y};
    }
    const x = point.x - pivot.x;
    const y = point.y - pivot.y;
    return {x: pivot.x + x * cosine - y * sine, y: pivot.y + x * sine + y * cosine};
  });
}

export function getFreeFormTurn(points, index) {
  const previous = points[index - 1];
  const pivot = points[index];
  const next = points[index + 1];
  const incoming = Math.atan2(pivot.y - previous.y, pivot.x - previous.x);
  const outgoing = Math.atan2(next.y - pivot.y, next.x - pivot.x);
  return Math.atan2(Math.sin(outgoing - incoming), Math.cos(outgoing - incoming));
}

// Distance to the first polygon boundary along a ray starting inside the shape.
export function getFreeFormRayLimit(pivot, direction, polygon) {
  const dx = Math.cos(direction);
  const dy = Math.sin(direction);
  let limit = Infinity;
  for (let index = 0; index < polygon.length; index++) {
    const from = polygon[index];
    const to = polygon[(index + 1) % polygon.length];
    const ex = to.x - from.x;
    const ey = to.y - from.y;
    const denominator = dx * ey - dy * ex;
    if (Math.abs(denominator) < 0.000001) {
      continue;
    }
    const x = from.x - pivot.x;
    const y = from.y - pivot.y;
    const distance = (x * ey - y * ex) / denominator;
    const segmentPosition = (x * dy - y * dx) / denominator;
    if (distance > 0.000001 && segmentPosition >= 0 && segmentPosition <= 1) {
      limit = Math.min(limit, distance);
    }
  }
  return limit;
}

// Match by position rather than winding: polygon creation can reverse the outline.
export function matchFreeFormOutline(outline, shapePoints) {
  if (outline.length !== shapePoints.length) {
    return null;
  }
  const used = new Set();
  const indices = shapePoints.map(point => {
    const index = outline.findIndex((candidate, candidateIndex) => !used.has(candidateIndex)
      && Math.hypot(candidate.x - point.x, candidate.y - point.y) < 0.02);
    used.add(index);
    return index;
  });
  return indices.includes(-1) ? null : indices;
}

// Reject intersections before committing a drag; simplifying would discard edge identities.
export function isSimpleFreeFormOutline(points) {
  const cross = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    for (let j = i + 2; j < points.length; j++) {
      if (i === 0 && j === points.length - 1) {
        continue;
      }
      const c = points[j];
      const d = points[(j + 1) % points.length];
      const overlaps = Math.max(Math.min(a.x, b.x), Math.min(c.x, d.x)) <= Math.min(Math.max(a.x, b.x), Math.max(c.x, d.x))
        && Math.max(Math.min(a.y, b.y), Math.min(c.y, d.y)) <= Math.min(Math.max(a.y, b.y), Math.max(c.y, d.y));
      if (overlaps && cross(a, b, c) * cross(a, b, d) <= 0 && cross(c, d, a) * cross(c, d, b) <= 0) {
        return false;
      }
    }
  }
  return true;
}
